import React, { useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Divider,
  Collapse,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import ButtonLoading from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import { PATH_CONSTANT } from 'constant/PathConstant';
import {
  useDeploySystem,
  useListAllSystemStatusDropdown,
  useListCurrentSystemDropdown,
  useSystemMemberListDropdown,
  useUsersForDeploySystem,
} from 'services/v1/TenantMaster/MasterTable/SystemTableDataService';
import { MEMBER_LIST_STATIC_RECORD } from 'data/MemberListRecord';
import { useServerListDropdown } from 'services/v1/TenantMaster/MasterTable/ServerTableDataService';
import { DropdownItem } from 'types/api/SystemTenant/AROKMS/TableDefinitionTypes';
import { DropdownCreatableMemberList } from 'pages/Tenant/AROKMS/DataInput/components/DropdownCreatableMemberList';
import { Field, Option } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { CreateAndDeploySystemValidationSchema } from 'config/FormValidaton/TenantMaster/MasterTable/SystemTableValidationScheman';

export const dataGridStyle = {
  '& .MuiCheckbox-root svg': {
    backgroundColor: 'transparent',
    color: '#98A2AE',
    borderRadius: 2,
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#EDF2FF',
    borderTop: '1px solid #E3EBF6',
    borderBottom: '1px solid #E3EBF6',
    textAlign: 'center',
    display: 'flex',
  },
};

export const autocompleteStyles = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    padding: '1px 2px',
  },
  '& .MuiAutocomplete-popper': {
    fontSize: '10px',
  },
};

export const textInputAutoCompleteStyles = {
  '& .MuiOutlinedInput': {
    padding: '1px 2px',
  },
};

const SYSTEM_DATA_SOURCE = {
  SCRATCH: 'SCRATCH',
  COPY_FROM_EXISTING_SYSTEM: 'COPY_FROM_EXISTING_SYSTEM',
};

const optionStyles = { '& > span': { fontSize: '14px', mr: 2, flexShrink: 0 } };

const sectionHeaderStyles = {
  color: '#3B4797',
  fontWeight: 600,
  fontSize: 14,
};

const sectionDescriptionStyles = {
  color: '#6B7280',
  fontSize: 12,
  mt: 0.5,
};

const fieldLabelStyles = {
  fontSize: 13,
  fontWeight: 500,
  color: '#374151',
  minWidth: 140,
  mb: 0.5,
};

const sectionPaperStyles = {
  p: 2.5,
  border: '1px solid #E3EBF6',
  borderRadius: 2,
  boxShadow: 'none',
};

interface FormValues {
  sourceSystemId: {
    value: string;
    label: string;
  } | null;
  serverId: {
    value: string;
    label: string;
  };
  assignUsers: {
    userId: string;
    roleId: string;
  }[];
  system: {
    value: string;
    label: string;
  };
  codeStatus: {
    value: string;
    label: string;
  };
  demoStatus: {
    value: string;
    label: string;
  };
  fieldStatus: {
    value: string;
    label: string;
  };
  dataSource: string;
  description?: string;
}

const formInitialValues: FormValues = {
  serverId: { value: '', label: '' },
  assignUsers: [],
  system: { value: '', label: '' },
  codeStatus: { value: '', label: '' },
  demoStatus: { value: '', label: '' },
  fieldStatus: { value: '', label: '' },
  dataSource: SYSTEM_DATA_SOURCE.SCRATCH,
  sourceSystemId: null,
  description: '',
};

function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <Box mb={2}>
      <Typography sx={sectionHeaderStyles}>{title}</Typography>
      <Typography sx={sectionDescriptionStyles}>{description}</Typography>
    </Box>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ mt: 2 }}>
      <Typography sx={fieldLabelStyles}>
        {label}
        {required && <span style={{ color: '#EF4444', marginLeft: 4 }}>*</span>}
      </Typography>
      {children}
    </Stack>
  );
}

function StepIndicator({ step, total, active }: { step: number; total: number; active: boolean }) {
  return (
    <Stack direction='row' alignItems='center' spacing={1} mb={2}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: active ? '#3B4797' : '#E3EBF6',
          color: active ? '#fff' : '#9CA3AF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {step}
      </Box>
      <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
        Step {step} of {total}
      </Typography>
    </Stack>
  );
}

function optionRenderInput(props: React.HTMLAttributes<HTMLLIElement>, option: DropdownItem) {
  return (
    <Box component='li' sx={optionStyles} {...props}>
      <span>{option.label}</span>
    </Box>
  );
}

export default function AddSystemPage() {
  const navigate = useNavigate();
  const { mutate: deploySystem, isLoading: isDeploying, error, isError } = useDeploySystem();

  const handleOnCancelInput = () => {
    navigate(PATH_CONSTANT.TENANT_MASTER.MASTER_TABLE.SYSTEM);
  };

  const handleOnSave = (data: FormValues) => {
    const payloadData = {
      codeStatus: data.codeStatus.value,
      fieldStatus: data.fieldStatus.value,
      demoStatus: data.demoStatus.value,
      dataSource: data.dataSource,
      serverId: data.serverId.value,
      sourceSystemId: data.sourceSystemId?.value || null,
      system: data.system.value,
      assignUsers: data.assignUsers,
      description: data.description,
    };
    deploySystem(payloadData, {
      onSuccess: () => {
        toast.success('System has been created successfully!');
        handleOnCancelInput();
      },
    });
  };

  const formik = useFormik<FormValues>({
    initialValues: formInitialValues,
    validationSchema: CreateAndDeploySystemValidationSchema,
    onSubmit: handleOnSave,
  });

  const { data: usersData, isLoading: isLoadingUsers } = useUsersForDeploySystem(formik.values.system?.value);
  const { data: listCurrentSystemDropdown } = useListCurrentSystemDropdown();
  const { data: serverOptions } = useServerListDropdown();
  const { data: systemMemberList, isLoading: isFetchingSystem } = useSystemMemberListDropdown();
  const { data: optionsStatus, isLoading: isFetchingOptionStatus } = useListAllSystemStatusDropdown();

  const disableDropdownStatus = useMemo(() => {
    return {
      codeStatus: formik.values.fieldStatus?.value !== MEMBER_LIST_STATIC_RECORD.FIELD_STATUS.RELEASED,
      demoStatus:
        formik.values.codeStatus?.value === MEMBER_LIST_STATIC_RECORD.CODE_STATUS.LOCKED &&
        formik.values.fieldStatus?.value !== MEMBER_LIST_STATIC_RECORD.FIELD_STATUS.RELEASED,
      fieldStatus:
        formik.values.codeStatus?.value === MEMBER_LIST_STATIC_RECORD.CODE_STATUS.LOCKED &&
        formik.values.demoStatus?.value === MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.LOCKED,
    };
  }, [formik.values]);

  const dropdownCreateableMemberListField = {
    name: 'system',
    options: (systemMemberList?.data as Option[]) || [],
    label: 'System',
    placeholder: 'Please Select',
    colSubjectId: '4028819c84e5373a0184e5373a3d0000',
    disabled: false,
  } as Field;

  const isAnyLoading = isFetchingSystem || isFetchingOptionStatus;

  const columns: GridColDef[] = [
    { field: 'fullName', headerName: 'Username', width: 180 },
    { field: 'roleName', headerName: 'Role Name', width: 180 },
    { field: 'userName', headerName: 'Email', width: 240 },
  ];

  const selectedIds = useMemo(() => {
    if (formik.values.assignUsers) {
      return formik.values.assignUsers.map((user) => user.userId);
    }
    return [];
  }, [formik.values.assignUsers]);

  useEffect(() => {
    if (
      formik.values.codeStatus?.value === MEMBER_LIST_STATIC_RECORD.CODE_STATUS.CHANGING &&
      formik.values.demoStatus?.value === MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.LOCKED
    ) {
      formik.setFieldValue(
        'demoStatus',
        optionsStatus?.data.demoStatus.find((item) => item.value === MEMBER_LIST_STATIC_RECORD.DEMO_STATUS.CHANGING),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.codeStatus, formik.values.demoStatus, optionsStatus?.data.demoStatus]);

  useEffect(() => {
    if (optionsStatus) {
      formik.setFieldValue(
        'codeStatus',
        optionsStatus.data.codeStatus.find((item) => item.label.toLocaleLowerCase() === 'changing'),
      );
      formik.setFieldValue(
        'demoStatus',
        optionsStatus.data.demoStatus.find((item) => item.label.toLocaleLowerCase() === 'changing'),
      );
      formik.setFieldValue(
        'fieldStatus',
        optionsStatus.data.fieldStatus.find((item) => item.label.toLocaleLowerCase() === 'not available'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsStatus]);

  useEffect(() => {
    if (formik.values.dataSource === SYSTEM_DATA_SOURCE.SCRATCH) {
      formik.setFieldValue('sourceSystemId', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.dataSource]);

  return (
    <TenantHomeDashboardNavigationBar>
      <Box sx={{ height: '100%', backgroundColor: '#F8F9FC' }}>
        <Box sx={{ maxWidth: 820, mx: 'auto', py: 3, px: 2 }}>
          {/* Page Header */}
          <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
            <Box>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797', fontSize: 18 }}>
                Create New System
              </Typography>
              <Typography sx={{ color: '#6B7280', fontSize: 13, mt: 0.5 }}>
                Set up a new system environment by completing the sections below. Fields marked with{' '}
                <span style={{ color: '#EF4444' }}>*</span> are required.
              </Typography>
            </Box>
            <Button size='small' onClick={handleOnCancelInput} variant='main-table-panel-border' disabled={isDeploying}>
              Cancel
            </Button>
          </Stack>

          {/* Section 1: Data Source */}
          <Paper sx={sectionPaperStyles}>
            <StepIndicator step={1} total={4} active={true} />
            <SectionHeader
              title='Data Source'
              description='Choose whether to create a brand new system from scratch or copy configuration from an existing system.'
            />
            <Divider sx={{ mb: 1 }} />

            <RadioGroup
              value={formik.values.dataSource}
              onChange={(event, value) => {
                formik.setFieldValue('dataSource', value);
              }}
              sx={{ mt: 1 }}
            >
              <Paper
                variant='outlined'
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  borderColor:
                    formik.values.dataSource === SYSTEM_DATA_SOURCE.SCRATCH ? '#3B4797' : '#E3EBF6',
                  backgroundColor:
                    formik.values.dataSource === SYSTEM_DATA_SOURCE.SCRATCH ? '#F0F2FF' : '#fff',
                  borderRadius: 1.5,
                  cursor: 'pointer',
                }}
                onClick={() => formik.setFieldValue('dataSource', SYSTEM_DATA_SOURCE.SCRATCH)}
              >
                <FormControlLabel
                  value={SYSTEM_DATA_SOURCE.SCRATCH}
                  control={<Radio size='small' sx={{ '&.Mui-checked': { color: '#3B4797' } }} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>From Scratch</Typography>
                      <Typography sx={{ fontSize: 11, color: '#6B7280' }}>
                        Create a clean system with no pre-existing configuration or data.
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
              </Paper>
              <Paper
                variant='outlined'
                sx={{
                  p: 1.5,
                  borderColor:
                    formik.values.dataSource === SYSTEM_DATA_SOURCE.COPY_FROM_EXISTING_SYSTEM
                      ? '#3B4797'
                      : '#E3EBF6',
                  backgroundColor:
                    formik.values.dataSource === SYSTEM_DATA_SOURCE.COPY_FROM_EXISTING_SYSTEM
                      ? '#F0F2FF'
                      : '#fff',
                  borderRadius: 1.5,
                  cursor: 'pointer',
                }}
                onClick={() =>
                  formik.setFieldValue('dataSource', SYSTEM_DATA_SOURCE.COPY_FROM_EXISTING_SYSTEM)
                }
              >
                <FormControlLabel
                  value={SYSTEM_DATA_SOURCE.COPY_FROM_EXISTING_SYSTEM}
                  control={<Radio size='small' sx={{ '&.Mui-checked': { color: '#3B4797' } }} />}
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>Copy from Existing System</Typography>
                      <Typography sx={{ fontSize: 11, color: '#6B7280' }}>
                        Pre-populate this system with configuration copied from another existing system.
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
              </Paper>
            </RadioGroup>

            <Collapse in={formik.values.dataSource === SYSTEM_DATA_SOURCE.COPY_FROM_EXISTING_SYSTEM}>
              <Box sx={{ mt: 2, pl: 1, borderLeft: '3px solid #3B4797', ml: 1.5 }}>
                <FormField label='Source System' required>
                  <Autocomplete
                    options={listCurrentSystemDropdown?.data || []}
                    // @ts-ignore
                    value={formik.values.sourceSystemId}
                    getOptionLabel={(option) => option.label}
                    clearIcon={null}
                    contentEditable={false}
                    onChange={(event, value) => {
                      formik.setFieldValue('sourceSystemId', value);
                    }}
                    renderOption={(props, option) => (
                      <Box component='li' sx={optionStyles} {...props}>
                        <span>{option.label}</span>
                      </Box>
                    )}
                    sx={autocompleteStyles}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={textInputAutoCompleteStyles}
                        placeholder='Select the system to copy from'
                        size='small'
                      />
                    )}
                  />
                </FormField>
              </Box>
            </Collapse>
          </Paper>

          {/* Section 2: System Details */}
          <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
            <StepIndicator step={2} total={4} active={true} />
            <SectionHeader
              title='System Details'
              description='Define the system identity, description, and deployment server.'
            />
            <Divider sx={{ mb: 1 }} />

            <DropdownCreatableMemberList<FormValues>
              required
              formik={formik}
              field={dropdownCreateableMemberListField}
              sx={{ width: '100%' }}
            />

            <FormField label='Description'>
              <TextField
                fullWidth
                size='small'
                placeholder='Enter a brief description of this system'
                name='description'
                value={formik.values.description}
                onChange={formik.handleChange}
              />
            </FormField>

            <FormField label='Server' required>
              <Autocomplete
                options={serverOptions || []}
                // @ts-ignore
                value={formik.values.serverId}
                disabled={isAnyLoading || !formik.values.system}
                getOptionLabel={(option) => option.label}
                clearIcon={null}
                contentEditable={false}
                onChange={(event, value) => {
                  formik.setFieldValue('serverId', value);
                }}
                renderOption={optionRenderInput}
                sx={autocompleteStyles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={textInputAutoCompleteStyles}
                    placeholder='Select the deployment server'
                    size='small'
                  />
                )}
              />
            </FormField>
          </Paper>

          {/* Section 3: Status Configuration */}
          <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
            <StepIndicator step={3} total={4} active={true} />
            <SectionHeader
              title='Status Configuration'
              description='Set the lifecycle status for code, demo, and field stages. Status progression follows a sequential lock flow — code must be locked before demo, and both must be locked before field can be released.'
            />
            <Divider sx={{ mb: 1 }} />

            <FormField label='Code Status' required>
              <Autocomplete
                disabled={!disableDropdownStatus.codeStatus}
                options={optionsStatus?.data.codeStatus || []}
                value={formik.values.codeStatus}
                getOptionLabel={(option: DropdownItem) => option.label}
                clearIcon={null}
                contentEditable={false}
                onChange={(event, value) => {
                  formik.setFieldValue('codeStatus', value);
                }}
                renderOption={optionRenderInput}
                sx={autocompleteStyles}
                renderInput={(params) => (
                  <TextField {...params} sx={textInputAutoCompleteStyles} placeholder='Select code status' size='small' />
                )}
              />
            </FormField>

            <FormField label='Demo Status' required>
              <Autocomplete
                options={optionsStatus?.data.demoStatus || []}
                // @ts-ignore
                value={formik.values.demoStatus}
                disabled={!disableDropdownStatus.demoStatus}
                getOptionLabel={(option) => option.label}
                clearIcon={null}
                contentEditable={false}
                onChange={(event, value) => {
                  formik.setFieldValue('demoStatus', value);
                }}
                renderOption={optionRenderInput}
                sx={autocompleteStyles}
                renderInput={(params) => (
                  <TextField {...params} sx={textInputAutoCompleteStyles} placeholder='Select demo status' size='small' />
                )}
              />
            </FormField>

            <FormField label='Field Status' required>
              <Autocomplete
                options={optionsStatus?.data.fieldStatus || []}
                // @ts-ignore
                value={formik.values.fieldStatus}
                disabled={!disableDropdownStatus.fieldStatus}
                getOptionLabel={(option) => option.label}
                clearIcon={null}
                contentEditable={false}
                onChange={(event, value) => {
                  formik.setFieldValue('fieldStatus', value);
                }}
                renderOption={optionRenderInput}
                sx={autocompleteStyles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={textInputAutoCompleteStyles}
                    placeholder='Select field status'
                    size='small'
                  />
                )}
              />
            </FormField>
          </Paper>

          {/* Section 4: Builder Assignment */}
          <Paper sx={{ ...sectionPaperStyles, mt: 2.5 }}>
            <StepIndicator step={4} total={4} active={!!formik.values.system?.value} />
            <SectionHeader
              title='Builder Assignment'
              description='Select the builders who will have access to configure and build within this system.'
            />
            <Divider sx={{ mb: 2 }} />

            <div style={{ height: 280, width: '100%' }}>
              {!isLoadingUsers && (
                <DataGridPro
                  loading={isLoadingUsers}
                  selectionModel={selectedIds}
                  sx={dataGridStyle}
                  density='compact'
                  rows={usersData?.data || []}
                  columns={columns}
                  onSelectionModelChange={(newSelection) => {
                    if (usersData?.data) {
                      const newAssignUsers = newSelection.map((id) => {
                        const user = usersData.data.find((u) => u.id === id);
                        return { userId: id as string, roleId: user?.roleId || '' };
                      });
                      formik.setFieldValue('assignUsers', newAssignUsers);
                    }
                  }}
                  pagination={false}
                  hideFooter
                  rowsPerPageOptions={[5]}
                  checkboxSelection={!isAnyLoading || (!!formik.values.system && !!formik.values.serverId)}
                />
              )}
            </div>
            {selectedIds.length > 0 && (
              <Typography sx={{ fontSize: 12, color: '#3B4797', mt: 1, fontWeight: 500 }}>
                {selectedIds.length} builder{selectedIds.length > 1 ? 's' : ''} selected
              </Typography>
            )}
          </Paper>

          {/* Error Alert */}
          {isError && (
            <Alert severity='error' sx={{ mt: 2.5, borderRadius: 2 }}>
              {error.response?.data.message}
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ mt: 3 }}>
            <Button disabled={isDeploying} onClick={handleOnCancelInput} variant='main-table-panel-border'>
              Cancel
            </Button>
            <ButtonLoading
              loading={isDeploying}
              variant='main-table-panel'
              onClick={() => formik.handleSubmit()}
              disabled={!CreateAndDeploySystemValidationSchema?.isValidSync(formik.values) || isDeploying}
            >
              Create & Deploy System
            </ButtonLoading>
          </Stack>
        </Box>
      </Box>
    </TenantHomeDashboardNavigationBar>
  );
}
